import { CardContent, Tooltip } from '@mui/material';
import styled, { css } from 'styled-components';
import { Typography, Chip } from './styles';
import { MinimalCard } from 'components/styled';
import { conditionallyRenderElement } from 'utils';
import { GenericTeamInterface } from 'types';
import { REMOVE_TAG_TEXT } from '@constants';

type TagsSectionPropsType = {
  selectedTeam?: GenericTeamInterface;
  /** Tags from fast search API - used as fallback if selectedTeam.tags unavailable */
  searchApiTags?: string[] | null;
  /** True if the rails API (selectedTeam) is still loading */
  isLoadingRailsApi?: boolean;
  /** True if the search API (searchApiTags) is still loading */
  isLoadingSearchApi?: boolean;
  /** @deprecated Use isLoadingRailsApi and isLoadingSearchApi instead */
  isLoading?: boolean;
};

const redChipStyles = css`
  .red-chip {
    background-color: #f44336;
    color: white;
  }
`;

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.3rem;

  .chip {
    margin: 0;
  }
  ${redChipStyles}
  .more-chip {
    cursor: default;
  }
`;

const TooltipContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 4px 0;

  .tooltip-chip {
    max-width: 280px;
  }
  ${redChipStyles}
`;

const StyledTagsCard = styled(MinimalCard)`
  cursor: default;
`;

/**
 * Maximum number of tags to display before showing "+N" indicator.
 *
 * UX rationale: 3 tags provides a good balance between showing meaningful
 * information at a glance while preventing horizontal overflow in card layouts.
 * This follows common UI patterns (e.g., email recipients, social media tags)
 * where showing 2-4 items inline keeps the interface clean and scannable.
 */
const MAX_VISIBLE_TAGS = 3;

const TagsCard = (props: TagsSectionPropsType) => {
  const tags = props.selectedTeam?.tags ?? props.searchApiTags ?? undefined;

  const isLoadingRails = props.isLoadingRailsApi ?? props.isLoading ?? false;
  const isLoadingSearch = props.isLoadingSearchApi ?? props.isLoading ?? false;
  const hasRailsData = props.selectedTeam !== undefined;
  const hasSearchData = props.searchApiTags !== undefined;
  const hasAnyData = hasRailsData || hasSearchData;

  const shouldShowLoading = !hasAnyData && (isLoadingRails || isLoadingSearch);
  const shouldShowError = !isLoadingRails && !isLoadingSearch && !hasAnyData;

  const visibleTags = tags?.slice(0, MAX_VISIBLE_TAGS) || [];
  const remainingTags = tags?.slice(MAX_VISIBLE_TAGS) || [];
  const hasMoreTags = remainingTags.length > 0;

  const renderTag = (tag: string, className = 'chip') => (
    <Chip
      key={tag}
      color="primary"
      className={tag === REMOVE_TAG_TEXT ? `${className} red-chip` : className}
      label={tag}
    />
  );

  const renderRemainingTagsTooltip = () => (
    <TooltipContainer>
      {remainingTags.map((tag) => renderTag(tag, 'tooltip-chip'))}
    </TooltipContainer>
  );

  return (
    <StyledTagsCard $hoverEffect={false}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Tags
        </Typography>
        {conditionallyRenderElement({
          isLoading: shouldShowLoading,
          error: shouldShowError ? 'Failed to fetch team tags' : '',
          content:
            tags && tags.length > 0 ? (
              <Container>
                {visibleTags.map((tag) => renderTag(tag))}
                {hasMoreTags && (
                  <Tooltip title={renderRemainingTagsTooltip()} placement="bottom">
                    <Chip className="chip more-chip" label={`+${remainingTags.length}`} />
                  </Tooltip>
                )}
              </Container>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No tags available
              </Typography>
            ),
        })}
      </CardContent>
    </StyledTagsCard>
  );
};

export default TagsCard;
